import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { FaCalendarAlt, FaArrowRight, FaClock } from "react-icons/fa";
import blogone from "../../../assest/images/blog1.png";
import blogtow from "../../../assest/images/blog2.png";
import blogthree from "../../../assest/images/blog-3.png";

function Blog() {
  const sectionRef = useRef(null);
  const [triggered, setTriggered] = useState(false);

  // Featured (middle) card is intentionally scaled bigger than the side
  // cards on desktop — but on mobile everything stacks into one column, so
  // that scale difference just made one card look randomly bigger than the
  // rest. Detect mobile and force every card to the same scale there.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || triggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          observer.disconnect();
        }
      },
      { threshold: [0.4] }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [triggered]);

  const blogPosts = [
    {
      id: 1,
      date: "15 August, 2024",
      readTime: "5 min read",
      category: "Traffic Insights",
      title: "What is invalid traffic? How publishers can prevent themselves from it?",
      description: "Invalid traffic includes both accidental clicks and fraudulent activity. Learn how to protect your revenue.",
      image: blogone,
    },
    {
      id: 2,
      date: "10 August, 2024",
      readTime: "3 min read",
      category: "Payments",
      title: "Myad360 introduces Bitcoin as a new payment method",
      description: "We are excited to announce that publishers can now receive their earnings via Bitcoin, providing flexibility.",
      image: blogtow,
      featured: true,
    },
    {
      id: 3,
      date: "05 August, 2024",
      readTime: "4 min read",
      category: "Ad Tech",
      title: "Google AdX vs Google AdSense: Which one is right?",
      description: "Choosing between AdX and AdSense can be tricky. We break down the key differences.",
      image: blogthree,
    },
  ];

  // Middle card: stays at center, just scales up from small to its final
  // "bigger" size. Side cards: start pulled IN toward the center (x offset
  // toward middle), then slide OUT to their real grid slot — so they visually
  // "emerge" from the middle card and travel left / right respectively.
  // On mobile, everything scales to 1 (same size) and there's no x offset
  // choreography since the grid is a single column anyway.
  const getVariants = (post, index) => {
    const isMiddle = !!post.featured;
    const startX = isMobile ? 0 : isMiddle ? 0 : index === 0 ? "140%" : "-140%";
    const visibleScale = isMobile ? 1 : isMiddle ? 1.08 : 0.92;
    const hiddenScale = isMobile ? 0.92 : isMiddle ? 0.75 : 0.8;

    return {
      hidden: { opacity: 0, scale: hiddenScale, x: startX, y: 20 },
      visible: { opacity: 1, scale: visibleScale, x: 0, y: 0 },
    };
  };

  return (
    <section
      id="blogID"
      ref={sectionRef}
      className="py-16 md:py-24 overflow-hidden border-t border-[#F5DFC5]"
      style={{
        background:
          "radial-gradient(90% 70% at 80% 20%, #FFF4E5 0%, transparent 55%), #FFFBF5",
      }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={triggered ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-14 md:mb-18 max-w-2xl mx-auto"
        >
          <div className="flex items-center w-full gap-4 mb-4">
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to left, #EA580C, transparent)",
              }}
            />
            <span className="text-xs font-bold tracking-wide text-[#92400E] whitespace-nowrap">
              Latest News &amp; Blog
            </span>
            <span
              className="hidden sm:inline-block h-[2px] flex-1"
              style={{
                background: "linear-gradient(to right, #EA580C, transparent)",
              }}
            />
          </div>

          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#1C1410] tracking-tight leading-[1.14]"
            style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
          >
            Stay Updated with Our{" "}
            <span className="relative inline-block whitespace-nowrap">
              <span
                className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]"
                style={{ fontStyle: "italic", fontWeight: 500 }}
              >
                Insights
              </span>
              <svg
                className="absolute left-0 -bottom-1.5 w-full"
                height="9"
                viewBox="0 0 150 9"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path
                  d="M2,6 C30,1 60,8 90,4 C120,0 148,7 148,3"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </span>
          </h2>
        </motion.div>

        {/* 3 Cards in One Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 sm:gap-5 md:gap-6 items-center relative">
          {blogPosts.map((post, i) => (
            <motion.div
              key={post.id}
              variants={getVariants(post, i)}
              initial="hidden"
              animate={triggered ? "visible" : "hidden"}
              transition={{
                delay: post.featured ? 0.1 : 0.35,
                duration: post.featured ? 0.6 : 0.8,
                ease: [0.22, 1, 0.36, 1],
              }}
              whileHover={{
                y: -10,
                scale: isMobile ? 1 : post.featured ? 1.12 : 0.98,
              }}
              className={`group relative flex flex-col bg-white transition-shadow duration-500 cursor-pointer ${
                post.featured
                  ? "sm:col-span-2 md:col-span-1 shadow-[0_20px_50px_rgba(217,119,6,0.15)] border-2 border-[#FDE8CD] z-30 py-6 px-2 rounded-[2rem] hover:shadow-[0_25px_60px_rgba(217,119,6,0.25)]"
                  : "shadow-sm border border-[#F0DCC8] py-6 z-10 rounded-[2rem] hover:shadow-2xl"
              }`}
            >
              <div className="relative overflow-hidden rounded-[1.6rem] h-44 sm:h-48 md:h-52 m-3 mb-0">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm text-[#1C1410] text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                  {post.category}
                </div>
              </div>

              <div className="p-5 sm:p-6 flex flex-col flex-grow">
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-[#A08E7A] text-[11px] font-bold mb-4 uppercase tracking-tighter">
                  <span className="flex items-center gap-1.5">
                    <FaCalendarAlt className="text-[#D97706]" /> {post.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <FaClock className="text-[#D97706]" /> {post.readTime}
                  </span>
                </div>
                <h3
                  className="text-[#1C1410] text-base sm:text-lg font-bold mb-3 group-hover:text-[#D97706] transition-colors line-clamp-2"
                  style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
                >
                  {post.title}
                </h3>
                <p className="text-[#4D3E30] text-sm leading-relaxed mb-6 line-clamp-2">
                  {post.description}
                </p>

                <div className="mt-auto">
                  {post.featured ? (
                    <button className="w-full py-3 rounded-lg font-bold text-xs tracking-wide uppercase text-white bg-gradient-to-r from-[#D97706] to-[#EA580C] shadow-md hover:shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center gap-2">
                      Read More <FaArrowRight size={11} />
                    </button>
                  ) : (
                    <button className="w-full py-3 rounded-lg border-2 border-[#D97706] text-[#D97706] text-xs font-bold tracking-wide uppercase hover:bg-[#D97706] hover:text-white transition-all flex items-center justify-center gap-2">
                      Read More <FaArrowRight size={11} />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Blog;